import { Component, JSX, onMount, createSignal, createEffect } from 'solid-js';
import { useLocation } from '@solidjs/router';
import Sidebar from '../navigation/Sidebar';
import Header from '../navigation/Header';
import RightSidebar from '../navigation/RightSidebar';
import Toast from '../ui/Toast';
import NewsModal from '../modals/NewsModal';
import socketService from '../../services/socket';
import { useSocketEvent } from '../../services/socket';
import { addToast } from '../../stores/toastStore';
import { getVoiceSettings, buildAnnouncementMessage } from '../../stores/voiceNotificationStore';
import { useSidebarStore } from '../../stores/sidebarStore';
import { newsState } from '../../services/newsService';
import { requestNotificationPermission, showNewLeadNotification } from '../../services/notificationService';
import { addRealtimeClick } from '../../stores/realtimeClicksStore';
import type { RealtimeClick } from '../../stores/realtimeClicksStore';

// Module-level flags to track if listeners have been registered (only once globally)
let toastListenerRegistered = false;
let clickListenerRegistered = false;
let notificationPermissionRequested = false;

// Flag to track socket connection state for listener re-registration
let lastSocketConnected = false;

interface LayoutProps {
  children?: JSX.Element;
}

/**
 * Main Layout Component
 * Provides the overall structure with sidebar, header, and realtime clicks panel
 *
 * NOTE: Sidebar state is managed by global sidebarStore
 * This ensures state persists across page navigation
 */
const Layout: Component<LayoutProps> = (props) => {
  const location = useLocation();
  // Use global sidebar store instead of local state
  const { sidebarOpen, rightSidebarOpen, toggleSidebar, toggleRightSidebar } = useSidebarStore();

  // State for real-time clicks panel expansion
  const [realtimeClicksExpanded, setRealtimeClicksExpanded] = createSignal(true);
  const [savedRealtimeClicksState, setSavedRealtimeClicksState] = createSignal(true);

  // Check if currently on /clicks page
  const isOnClicksPage = () => location.pathname === '/clicks';

  // Save expanded state when leaving /clicks page, restore when coming back
  createEffect(() => {
    if (!isOnClicksPage()) {
      // Not on /clicks page - save the current state for restoration
      setSavedRealtimeClicksState(realtimeClicksExpanded());
    }
  });

  // Conversion queue for sequential announcements
  let conversionQueue: Array<{ user: string; payout: number }> = [];
  let batchTimeout: ReturnType<typeof setTimeout> | null = null;
  let isAnnouncing = false;
  const BATCH_WINDOW = 300; // 300ms to collect conversions

  /**
   * Announce the next conversion in the queue
   * Called recursively to announce all conversions one by one
   */
  const announceNextConversion = () => {
    if (conversionQueue.length === 0) {
      isAnnouncing = false;
      return;
    }

    const settings = getVoiceSettings();

    // Check if voice announcements are enabled
    if (!settings.enabled || !settings.soundEnabled) {
      conversionQueue = [];
      isAnnouncing = false;
      return;
    }

    if (typeof window === 'undefined') return;

    // Check if speech synthesis is supported
    const synth = window.speechSynthesis;
    if (!synth) {
      return;
    }

    // Get next conversion from queue
    const conversion = conversionQueue.shift();
    if (!conversion) return;

    // Build message for this conversion
    const message = buildAnnouncementMessage(conversion.user, conversion.payout, settings.format);
    const utterance = new SpeechSynthesisUtterance(message);

    // Apply user settings to utterance
    utterance.rate = settings.rate;
    utterance.pitch = settings.pitch;
    utterance.volume = settings.volume;
    utterance.lang = settings.language;

    // Trigger vibration feedback on supported devices
    if (settings.vibration && navigator.vibrate) {
      navigator.vibrate(200); // Vibrate for 200ms per conversion
    }

    // When this utterance finishes, announce the next one
    utterance.onend = () => {
      announceNextConversion();
    };

    // Speak this conversion
    synth.speak(utterance);
  };

  /**
   * Queue a conversion for sequential announcement
   * Collects conversions within BATCH_WINDOW and announces them one by one
   */
  const queueConversion = (user: string, payout: number) => {
    conversionQueue.push({ user, payout: parseFloat(payout.toFixed(2)) });

    // Clear existing timeout
    if (batchTimeout) {
      clearTimeout(batchTimeout);
    }

    // Set new timeout to start announcements after batch window
    batchTimeout = setTimeout(() => {
      if (!isAnnouncing) {
        isAnnouncing = true;
        announceNextConversion();
      }
    }, BATCH_WINDOW);
  };

  onMount(async () => {
    // Initialize socket connection
    const socket = socketService.initialize();

    // Check if socket just reconnected (was disconnected, now connected)
    const isNowConnected = socketService.state.isConnected();
    if (isNowConnected && !lastSocketConnected) {
      // Socket reconnected - reset listener flags to allow re-registration
      console.log('[Layout] Socket reconnected, re-registering listeners...');
      toastListenerRegistered = false;
      clickListenerRegistered = false;
    }
    lastSocketConnected = isNowConnected;

    // Request browser notification permission once on app load
    if (!notificationPermissionRequested) {
      notificationPermissionRequested = true;
      try {
        await requestNotificationPermission();
      } catch (error) {
        console.warn('Failed to request notification permission:', error);
      }
    }

    // Global conversion listener - shows toast for all conversions across all pages
    // Only register ONCE globally to prevent duplicate toasts on component remounts
    if (!toastListenerRegistered) {
      toastListenerRegistered = true;

      // Handle conversion notifications
      const handleConversion = (data: any) => {
        const payout = parseFloat(data.payout).toFixed(2);

        // Show visual notification (only 1 toast per conversion)
        addToast(`✓ Conversion: ${data.user} - $${payout}`, 'success', 4000);

        // Show browser notification for new lead
        showNewLeadNotification({
          user: data.user,
          amount: parseFloat(payout),
          country: data.country,
          platform: data.traffic_source,
        });

        // Queue conversion for batch voice announcement
        queueConversion(data.user, parseFloat(payout));
      };

      // Register conversion listeners using useSocketEvent (auto-handles reconnections)
      useSocketEvent('conversion-recorded', handleConversion);
      useSocketEvent('new-conversion', handleConversion);
    }

    // Global real-time clicks listener - updates sidebar across all pages
    // Only register ONCE globally to prevent duplicate handlers on component remounts
    if (!clickListenerRegistered) {
      clickListenerRegistered = true;

      // Handle new clicks - add to global store
      const handleClick = (data: RealtimeClick) => {
        addRealtimeClick(data, 50);
      };

      // Register click listeners using useSocketEvent for automatic lifecycle management
      // This automatically handles socket reconnections and cleanup
      useSocketEvent<RealtimeClick>('click-recorded', handleClick);
      useSocketEvent<RealtimeClick>('new-click', handleClick);
    }

    // Note: We don't disconnect the socket on unmount to preserve the connection
    // across page navigations. This keeps the real-time data flowing for the
    // sidebar components (Real-time Clicks and Leads by Countries).
    // The socket will only disconnect when the user logs out.
  });

  return (
    <div class="min-h-screen bg-body dark:bg-dark flex flex-col lg:flex-row font-body">
      {/* Toast Notifications */}
      <Toast />

      {/* Left Sidebar - Navigation */}
      <Sidebar
        isOpen={sidebarOpen()}
        onToggle={toggleSidebar}
      />

      {/* Container for main content */}
      <div class="flex flex-1 relative min-w-0">
        {/* Main Content Area - Shrinks with right margin when right sidebar opens */}
        <div
          class={`flex-1 transition-[margin] duration-300 will-change-[margin] ${
            sidebarOpen() ? 'lg:ml-64' : 'lg:ml-16'
          } ${
            rightSidebarOpen() ? 'lg:mr-80' : 'lg:mr-0'
          } flex flex-col relative pt-16 min-w-0 overflow-hidden`}
        >
          {/* Header - Fixed at top */}
          <Header
            onMenuClick={toggleSidebar}
            sidebarOpen={sidebarOpen()}
            rightSidebarOpen={rightSidebarOpen()}
            onRightSidebarToggle={toggleRightSidebar}
          />

          {/* Page Content */}
          <main class="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto brand-scrollbar">{props.children}</main>
        </div>
      </div>

      {/* Right Sidebar - Fixed positioned sidebar (stays visible when scrolling) */}
      <RightSidebar
        isOpen={rightSidebarOpen()}
        onToggle={toggleRightSidebar}
        shouldMinimizeRealtimeClicks={isOnClicksPage()}
        savedRealtimeClicksState={savedRealtimeClicksState}
        onRealtimeClicksStateChange={setRealtimeClicksExpanded}
      />

      {/* News Modal - Rendered at root level for full-page backdrop */}
      <NewsModal
        isOpen={newsState.showModal()}
        onClose={() => newsState.setShowModal(false)}
        news={newsState.latestNews()}
      />
    </div>
  );
};

export default Layout;
